import { type Transition, type Variants, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import Button from '../Button/Button';
import type { CarouselProps } from './Carousel.types';

/**
 * Carousel:
 *  - Easily create a carousel with custom slides.
 *  - Supports automatic sliding, navigation arrows, and slide indicators.
 */
const Carousel = ({
  testId = 'carousel-id',
  activeIndex = 0,
  extraClassNames = '',
  onSlide = () => {},
  autoSlide,
  showIndicators = true,
  continuous = true,
  interval = 4000,
  prevIcon = 'fc-arrow-dropdown tw:rotate-90',
  nextIcon = 'fc-arrow-dropdown tw:rotate-270',
  showArrows = true,
  iconExtraClassNames = '',
  carouselItems = [],
}: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(activeIndex);

  const changeSlide = (nextIndex: number) => {
    const newIndex = (nextIndex + carouselItems.length) % carouselItems.length;
    setCurrentIndex(newIndex);
    onSlide(newIndex, newIndex > currentIndex ? 'right' : 'left');
  };

  useEffect(() => {
    if (autoSlide) {
      const slideInterval = setInterval(() => {
        if (continuous || currentIndex < carouselItems.length - 1) {
          changeSlide(currentIndex + 1);
        } else {
          clearInterval(slideInterval);
        }
      }, interval);
      return () => clearInterval(slideInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSlide, currentIndex, continuous, interval, carouselItems.length]);

  useEffect(() => {
    if (activeIndex !== currentIndex) {
      setCurrentIndex(activeIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const onBackClick = () => {
    if (currentIndex === 0 && !continuous) {
      return;
    }
    let prevIndex = currentIndex - 1;
    if (prevIndex < 0) {
      prevIndex = carouselItems.length - 1;
    }
    changeSlide(prevIndex);
  };

  const onForwardClick = () => {
    if (currentIndex === carouselItems.length - 1) {
      if (!continuous) {
        return;
      }
      changeSlide(0);
    } else {
      changeSlide(currentIndex + 1);
    }
  };

  const springTransition: Transition = {
    type: 'spring',
    damping: 20,
    stiffness: 100,
  };

  const slideVariants: Variants = {
    initial: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    animate: {
      x: 0,
      opacity: 1,
      transition: springTransition,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
      transition: springTransition,
    }),
  };

  return (
    <div
      data-testid={testId}
      className={`tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:overflow-hidden ${extraClassNames}`}>
      <div className="tw:flex tw:items-center tw:w-full tw:h-max tw:gap-1">
        {showArrows && (
          <Button
            icon={prevIcon}
            size="lg"
            variant="no-border"
            testId="carousel-prev"
            extraClassNames={`${
              currentIndex === 0 && !continuous ? 'tw:invisible' : ''
            } ${iconExtraClassNames} tw:animate-fadeIn`}
            onClick={onBackClick}
          />
        )}
        <div className="tw:h-full tw:w-full tw:overflow-hidden">
          <motion.div
            key={currentIndex}
            custom={1} // Direction of animation (1 for forward, -1 for backward)
            initial="initial"
            animate="animate"
            exit="exit"
            variants={slideVariants}
            className="tw:w-full">
            {carouselItems[currentIndex]}
          </motion.div>
        </div>
        {showArrows && (
          <Button
            icon={nextIcon}
            testId="carousel-next"
            size="lg"
            variant="no-border"
            extraClassNames={`${
              currentIndex === carouselItems.length - 1 && !continuous ? 'tw:invisible' : ''
            } ${iconExtraClassNames} tw:animate-fadeIn`}
            onClick={onForwardClick}
          />
        )}
      </div>
      {showIndicators && (
        <div className="tw:flex tw:gap-1 tw:mt-2">
          {carouselItems.map((_, i) => (
            <div
              key={i}
              className={`tw:w-2 tw:h-2 tw:rounded-full tw:cursor-pointer ${
                i === currentIndex ? 'tw:bg-sq-theme-dark' : 'tw:bg-sq-darkish-gray'
              }`}
              onClick={() => changeSlide(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
